import { useState } from "react";
import { ArrowRight, Check, Info } from "lucide-react";

import type { AfstandContact, AfstandVraag } from "../../afstand/afstandApi";
import { vragenCompleet, watOntbreekt, type Dier } from "../../afstand/afstandDieren";
import type { Bijlagen } from "./AfstandDocumentenStap";

/**
 * Stap 4: nalopen en versturen.
 *
 * <p><b>Indienen is geen afstand.</b> Dat staat er met zoveel woorden, en het is de belangrijkste zin
 * van dit scherm. Wie hier op een knop drukt heeft zijn dier niet weggedaan — het asiel gaat de
 * aanvraag beoordelen en neemt contact op. Zonder die zin leest "Aanvraag indienen" als een punt van
 * geen terugkeer, en dat is het niet.
 *
 * <p><b>De verklaring is een bewuste vinkje-actie</b> en staat niet stil aangevinkt: de eigenaar
 * verklaart dat hij naar waarheid heeft ingevuld, en dat hoort hij zelf te doen.
 *
 * <p><b>Wat nog ontbreekt staat erbij</b>, per onderdeel. Een knop die grijs is zonder te zeggen
 * waarom laat iemand zoeken in vier stappen tegelijk.
 */
export function AfstandIndienenStap({ contact, dieren, bijlagen, vragenVan, opvangNaam, bezig, onIndienen }: {
  contact: AfstandContact | null;
  dieren: Dier[];
  bijlagen: Bijlagen;
  vragenVan: (diersoort: string) => AfstandVraag[];
  opvangNaam: string;
  bezig: boolean;
  onIndienen: () => void;
}) {
  const [verklaard, setVerklaard] = useState(false);

  const gegevensKlaar = contact != null && contact.naam.trim() !== "" && (contact.telefoon ?? "") !== "";

  // Per dier wat er nog mist; leeg betekent klaar.
  const tekort = dieren.map((d) => ({ dier: d, mist: watOntbreekt(d, vragenVan(d.diersoort)) }));
  const dierenKlaar = dieren.length > 0 && tekort.every((t) => t.mist.length === 0);

  // Welke dieren nog geen enkel bestand hebben. Welke dat zijn is wat de eigenaar moet weten.
  const zonderBestand = dieren.filter((d) =>
    Object.entries(bijlagen)
      .filter(([k]) => k.startsWith(`${d.id}:`))
      .every(([, v]) => v.length === 0));
  const documentenKlaar = zonderBestand.length === 0;

  const alles = gegevensKlaar && dierenKlaar && documentenKlaar;

  return (
    <div className="afst__nalopen">
      <div className="afst__nalopen-lijst">
        <Onderdeel titel="Uw gegevens" klaar={gegevensKlaar}>
          {gegevensKlaar && contact
            ? [contact.naam, contact.email, contact.telefoon, contact.woonplaats]
                .filter(Boolean).join(" \u00b7 ")
            : <em className="afst__mist">Nog nodig: uw naam, telefoonnummer en adres.</em>}
        </Onderdeel>

        <Onderdeel titel={`Uw dieren (${dieren.length})`} klaar={dierenKlaar}>
          <ul className="afst__nalopen-dieren">
            {tekort.map(({ dier: d, mist }, i) => (
              <li key={d.id}>
                <span className="afst__dierfoto afst__dierfoto--klein" aria-hidden="true" />
                <span>
                  <strong>{d.naam || `Dier ${i + 1}`}</strong>
                  {mist.length === 0
                    ? [d.diersoort, vragenCompleet(d, vragenVan(d.diersoort)) ? "afstandsvragen voltooid" : null]
                        .filter(Boolean).join(" \u00b7 ")
                    : <em className="afst__mist">Nog nodig: {mist.join(", ")}</em>}
                </span>
              </li>
            ))}
          </ul>
        </Onderdeel>

        <Onderdeel titel="Documenten" klaar={documentenKlaar}>
          {documentenKlaar
            ? "De gevraagde bestanden zijn per dier toegevoegd."
            : (
              <em className="afst__mist">
                Nog geen bestand toegevoegd voor{" "}
                {zonderBestand.map((d, i) => d.naam || `dier ${i + 1}`).join(", ")}.
              </em>
            )}
        </Onderdeel>
      </div>

      <aside className="afst__paneel afst__indienen">
        <h3>Klaar om in te dienen?</h3>
        <p className="afst__paneeltekst">
          Controleer of alles klopt. Na het indienen gaat deze aanvraag naar
          {opvangNaam ? ` ${opvangNaam}` : " het asiel"}.
        </p>

        <p className="afst__tip">
          <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
          Indienen is geen definitieve afstand. Het asiel neemt contact met u op over de beoordeling
          en vervolgstappen.
        </p>

        <label className="afst__verklaring">
          <input type="checkbox" checked={verklaard} onChange={(e) => setVerklaard(e.target.checked)} />
          Ik verklaar dat ik de gegevens naar waarheid heb ingevuld en de privacyverklaring heb
          gelezen.
        </label>

        <button type="button" className="afst__knop" disabled={!alles || !verklaard || bezig}
                onClick={onIndienen}>
          {bezig ? "Bezig…" : "Aanvraag indienen"}
          <ArrowRight size={18} aria-hidden="true" />
        </button>

        {!alles && (
          <p className="afst__melding">
            Er ontbreekt nog iets. Hiernaast staat per onderdeel precies wat er nog nodig is; klik
            links op die stap om het aan te vullen.
          </p>
        )}
      </aside>
    </div>
  );
}

function Onderdeel({ titel, klaar, children }: {
  titel: string;
  klaar: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="afst__paneel afst__onderdeel">
      <h3>
        {titel}
        <span className={`afst__merkje${klaar ? " is-klaar" : ""}`}>
          {klaar ? <><Check size={13} aria-hidden="true" /> Compleet</> : "Nog niet compleet"}
        </span>
      </h3>
      <div className="afst__onderdeel-inhoud">{children}</div>
    </section>
  );
}
